/**
 * "How this pool works" for a Squares pool (ISSUE-1281): the pool Rules
 * content, drawn by the shared `features/poolRules/PoolRulesBody.tsx`. The
 * pool part (the Commissioner's preamble and the joining summary) comes from
 * `POST /pool/rules-get`; the Squares part comes from `POST /squares/rules-get`,
 * which builds the sentences on the server from the active season. This card
 * splits nothing and words nothing itself. Both reads are live on every mount,
 * so a setting an admin just changed shows here at once.
 */
import { useSquaresRulesGet } from "@sportsmet/contest-sdk/private";
import { usePoolRulesGet } from "@sportsmet/pool-sdk/private";
import { Card } from "@sportsmet/ui";
import type { ReactNode } from "react";

import { extractErrorCode } from "../../errors/apiErrorBus";
import { PoolRulesBody } from "../poolRules/PoolRulesBody";
import { resolvePoolRulesState } from "../poolRules/poolRulesState";
import { SQUARES_SETTINGS_RULES_HEADING } from "./squaresCopy";

export const SquaresRulesCard = ({ poolId }: { poolId: string }): ReactNode => {
  const rules = usePoolRulesGet(
    { poolId },
    { staleTime: 0, refetchOnMount: "always" },
  );
  const squaresRules = useSquaresRulesGet(
    { poolId },
    { staleTime: 0, refetchOnMount: "always" },
  );
  const squaresText = squaresRules.data?.data?.autoGeneratedText ?? null;
  const state = resolvePoolRulesState({
    isLoading: rules.isLoading || squaresRules.isLoading,
    isTransportError: rules.isError || squaresRules.isError,
    errorCode:
      extractErrorCode(rules.data) ?? extractErrorCode(squaresRules.data),
    commissionerPreamble: rules.data?.data?.commissionerPreamble ?? null,
    autoGeneratedSummary: rules.data?.data?.autoGeneratedSummary ?? null,
    contest:
      squaresText === null
        ? null
        : { autoGeneratedText: squaresText, commissionerPreamble: null },
  });

  return (
    <Card
      title={SQUARES_SETTINGS_RULES_HEADING}
      data-testid="squares-settings-rules"
    >
      <PoolRulesBody
        state={state}
        onRetry={() => {
          void rules.refetch();
          void squaresRules.refetch();
        }}
      />
    </Card>
  );
};
